import React from 'react';
import { motion } from 'framer-motion';
import { Gift, CheckCircle2, AlertCircle, Compass, BookOpen, RotateCcw } from 'lucide-react';
const bonuses = [{
  id: 1,
  title: 'SOS — Kit de Primeros Auxilios Espirituales (24h)',
  subtitle: 'Para cuando estás al límite y no puedes esperar 40 días',
  image: 'https://cdn.magicpatterns.com/uploads/5Avr1RWRrknF99fKX2TK8F/Bonus_1..png',
  value: '$3.50',
  icon: AlertCircle,
  description: 'Hay momentos donde la crisis no espera. Donde a las 2am tu mente no para. Este kit fue diseñado para ese momento exacto. Un protocolo de 10 minutos que baja la intensidad del caos interno.',
  features: ['El Protocolo RESET de 10 minutos (paso a paso)', '3 oraciones/declaraciones de emergencia', '12 frases ancla listas para guardar en tu celular', 'Lista "NO hagas esto hoy" (anti-daño en crisis)', 'Plan de 24h mínimo (solo 3 cosas)', 'Printable imprimible para tenerlo siempre a mano'],
  promise: 'En 10 minutos vas a sentir que puedes respirar otra vez.'
}, {
  id: 2,
  title: 'Mapa del Desierto: Diagnóstico en 5 Min',
  subtitle: 'Porque antes de avanzar, necesitas saber exactamente dónde estás',
  image: 'https://cdn.magicpatterns.com/uploads/are7Anx4eCRuWjyraDovD5/bonus_2..png',
  value: '$3.50',
  icon: Compass,
  description: 'La razón por la que la mayoría camina en círculos en su crisis es simple: no saben en qué fase están. Este diagnóstico te ubica en 1 de 4 fases exactas y te da la acción específica que necesitas.',
  features: ['Test de 12 preguntas (5 minutos)', 'Guía completa por fase (qué significa + qué NO hacer)', 'Tu acción #1 según tu fase (hoy mismo)', 'Plan micro de 3 días para arrancar con momentum', 'Printable "mi fase / mi acción" para tenerlo visible'],
  promise: 'En 5 minutos tendrás más claridad sobre tu proceso que en meses de confusión.'
}, {
  id: 3,
  title: 'Journaling Profundo: 21 Preguntas que te sacan del fondo',
  subtitle: 'Para cuando tu mente es ruido y necesitas ordenarte por dentro',
  image: 'https://cdn.magicpatterns.com/uploads/x7kNLwqcTDezDhX5kTXffm/Bonus_3..png',
  value: '$3.50',
  icon: BookOpen,
  description: 'Hay preguntas que liberan. Y hay preguntas que transforman. Estas 21 son del segundo tipo. No son genéricas. Van directo a lo que duele, lo que confunde y lo que te sostiene.',
  features: ['21 preguntas profundas (organizadas por etapa)', 'Plantilla "1 página diaria" (imprimible)', 'Tracker de 7 días (hábitos mínimos de estabilidad)', '5 frases ancla de emergencia', 'Formato móvil para usarlo cuando te pega la crisis'],
  promise: 'Responde 3 preguntas hoy y sentirás una claridad que no tenías hace semanas.'
}, {
  id: 4,
  title: 'Guía Anti-Retroceso: Vuelve en 15 Min',
  subtitle: 'Porque la mayoría no abandona por falta de voluntad. Lo abandona por no saber qué hacer cuando cae',
  image: 'https://cdn.magicpatterns.com/uploads/3fHtovhsYL4Ntq3LkVjgam/Bonus_4..png',
  value: '$3.50',
  icon: RotateCcw,
  description: 'Esta guía resuelve el problema que nadie habla: la recaída. Ese momento donde vuelves a sentirte mal y tu mente te dice "otra vez lo mismo". Con este protocolo de 15 minutos sabrás exactamente qué hacer.',
  features: ['Checklist de señales tempranas de recaída', 'Protocolo RESET de 15 minutos (paso a paso)', '3 scripts de auto-habla (para cuando tu mente te ataca)', 'Lista "NO hagas esto cuando recaigas" (anti-daño)', 'Ruta rápida "Vuelta al Día 1" (retoma sin drama)', 'Mini plan 48h post-recaída'],
  promise: 'La próxima vez que caigas, no te quedarás en el piso.'
}];
export function BonusSection() {
  return <section className="py-8 md:py-16 px-4 md:px-6" style={{
    backgroundColor: 'var(--bg-deep)'
  }} data-id="element-48">
      <div className="max-w-[900px] mx-auto" data-id="element-49">
        {/* Hero Section */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-center mb-8 md:mb-12" data-id="element-50">
          <div className="inline-flex items-center justify-center w-14 h-14 md:w-16 md:h-16 rounded-full bg-[var(--accent-soft)] text-[var(--accent)] mb-4 md:mb-6" data-id="element-51">
            <Gift className="w-7 h-7 md:w-8 md:h-8" data-id="element-52" />
          </div>
          <h2 className="text-2xl md:text-4xl font-serif text-[var(--text-heading)] mb-3 md:mb-4" data-id="element-53">
            Y por si fuera poco...
          </h2>
          <p className="text-base md:text-xl text-[var(--text-primary)] leading-relaxed max-w-2xl mx-auto mb-4" data-id="element-54">
            Cuando decidí crear este método, quise asegurarme de una sola cosa:
          </p>
          <p className="text-lg md:text-2xl font-serif italic text-[var(--accent)] mb-6" data-id="element-55">
            Que no te quedara ninguna excusa para no empezar hoy.
          </p>
        </motion.div>

        {/* Agitation Copy */}
        <motion.div initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} className="bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded-xl p-5 md:p-8 mb-8 md:mb-12" data-id="element-56">
          <p className="text-sm md:text-base text-[var(--text-primary)] leading-relaxed mb-4" data-id="element-57">
            Sé lo que pasa cuando compras algo y lo dejas para después. Lo abres
            en 3 días. O en una semana. O no lo abres.
          </p>
          <p className="text-sm md:text-base text-[var(--text-primary)] leading-relaxed mb-4" data-id="element-58">
            No porque no quieras. Sino porque{' '}
            <strong className="text-[var(--text-heading)]" data-id="element-59">
              en medio de la crisis, el cerebro busca razones para esperar
            </strong>
            .
          </p>
          <div className="space-y-2 mb-4 pl-4 border-l-2 border-[var(--accent)]/30" data-id="element-60">
            <p className="text-sm italic text-[var(--text-muted)]" data-id="element-61">
              "Ahora no estoy listo."
            </p>
            <p className="text-sm italic text-[var(--text-muted)]" data-id="element-62">
              "No sé por dónde empezar."
            </p>
            <p className="text-sm italic text-[var(--text-muted)]" data-id="element-63">
              "¿Y si recaigo a mitad del proceso?"
            </p>
            <p className="text-sm italic text-[var(--text-muted)]" data-id="element-64">
              "Estoy demasiado mal para empezar algo."
            </p>
          </div>
          <p className="text-base md:text-lg text-[var(--accent)] font-semibold" data-id="element-65">
            Por eso decidí incluir 4 herramientas que atacan esas excusas una
            por una.
          </p>
        </motion.div>

        {/* Value Announcement */}
        <div className="text-center mb-8 md:mb-12" data-id="element-66">
          <p className="text-sm md:text-base text-[var(--text-muted)] mb-2" data-id="element-67">
            Estas 4 herramientas tienen un valor real de{' '}
            <span className="line-through" data-id="element-68">$14.00</span>
          </p>
          <p className="text-xl md:text-2xl font-serif text-[var(--accent)] font-bold" data-id="element-69">
            Hoy las recibes completamente GRATIS con tu método
          </p>
        </div>

        {/* Bonus Cards */}
        <div className="space-y-6 md:space-y-8 mb-10 md:mb-14" data-id="element-70">
          {bonuses.map((bonus, index) => <motion.div key={bonus.id} initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: index * 0.1
        }} className="bg-[var(--bg-card)] rounded-xl md:rounded-2xl border border-[var(--accent)]/20 overflow-hidden shadow-xl" data-id="element-71">
              <div className="grid md:grid-cols-[300px_1fr] gap-0" data-id="element-72">
                {/* Image Side */}
                <div className="relative h-48 md:h-auto bg-[var(--bg-elevated)] p-6 md:p-8 flex items-center justify-center border-b md:border-b-0 md:border-r border-[var(--border-subtle)]" data-id="element-73">
                  <div className="absolute top-3 left-3 bg-[var(--accent)] text-[var(--bg-deep)] text-[10px] md:text-xs font-bold px-2.5 py-1 rounded-full uppercase tracking-wider z-10" data-id="element-74">
                    Bono #{bonus.id} — Gratis
                  </div>
                  <img src={bonus.image} alt={bonus.title} className="w-full max-w-[200px] md:max-w-[240px] h-auto object-contain drop-shadow-2xl" data-id="element-75" />
                </div>

                {/* Content Side */}
                <div className="p-5 md:p-8" data-id="element-76">
                  <div className="flex items-center gap-2 text-[var(--accent)] mb-3" data-id="element-77">
                    <bonus.icon className="w-5 h-5" data-id="element-78" />
                    <span className="text-xs font-bold uppercase tracking-widest" data-id="element-79">
                      Valor: {bonus.value} → Hoy: GRATIS
                    </span>
                  </div>

                  <h3 className="text-lg md:text-2xl font-serif font-bold text-[var(--text-heading)] mb-2 leading-tight" data-id="element-80">
                    {bonus.title}
                  </h3>

                  <p className="text-[var(--accent)] font-serif italic text-sm md:text-base mb-4" data-id="element-81">
                    {bonus.subtitle}
                  </p>

                  <p className="text-[var(--text-muted)] text-sm md:text-base mb-5 leading-relaxed" data-id="element-82">
                    {bonus.description}
                  </p>

                  <div className="mb-5" data-id="element-83">
                    <p className="text-xs uppercase tracking-wider text-[var(--text-muted)] mb-3 font-bold" data-id="element-84">
                      Lo que recibes:
                    </p>
                    <ul className="space-y-2" data-id="element-85">
                      {bonus.features.map((feature, i) => <li key={i} className="flex items-start gap-2 text-sm text-[var(--text-primary)]" data-id="element-86">
                          <CheckCircle2 className="w-4 h-4 text-[var(--accent)] flex-shrink-0 mt-0.5" data-id="element-87" />
                          <span data-id="element-88">{feature}</span>
                        </li>)}
                    </ul>
                  </div>

                  {/* Promise */}
                  <div className="bg-[var(--accent-soft)] border border-[var(--accent)]/20 rounded-lg p-4" data-id="element-89">
                    <p className="text-sm font-semibold text-[var(--text-heading)]" data-id="element-90">
                      <span className="text-[var(--accent)]" data-id="element-91">La promesa:</span>{' '}
                      {bonus.promise}
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>)}
        </div>

        {/* Value Stack Table */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="bg-[var(--bg-surface)] border border-[var(--accent)]/25 rounded-xl overflow-hidden mb-8 md:mb-12" data-id="element-92">
          <div className="p-4 md:p-6 border-b border-[var(--border-subtle)]" data-id="element-93">
            <h3 className="text-lg md:text-xl font-serif text-[var(--text-heading)] text-center" data-id="element-94">
              Esto es lo que recibes hoy:
            </h3>
          </div>
          <div className="divide-y divide-[var(--border-subtle)]" data-id="element-95">
            <div className="flex items-center justify-between p-4 md:p-5" data-id="element-96">
              <span className="text-sm md:text-base text-[var(--text-primary)]" data-id="element-97">
                Método principal (40 días)
              </span>
              <span className="text-sm text-[var(--text-muted)] line-through" data-id="element-98">
                $27.00
              </span>
            </div>
            {bonuses.map(bonus => <div key={bonus.id} className="flex items-center justify-between p-4 md:p-5" data-id="element-99">
                <span className="text-sm md:text-base text-[var(--text-primary)]" data-id="element-100">
                  Bono #{bonus.id}
                </span>
                <span className="text-sm text-[var(--text-muted)] line-through" data-id="element-101">
                  {bonus.value}
                </span>
              </div>)}
            <div className="bg-[var(--accent-soft)] p-4 md:p-5" data-id="element-102">
              <div className="flex items-center justify-between" data-id="element-103">
                <span className="font-bold text-base md:text-lg text-[var(--text-heading)]" data-id="element-104">
                  VALOR TOTAL DE BONOS
                </span>
                <span className="font-bold text-lg md:text-xl text-[var(--accent)]" data-id="element-105">
                  $14.36
                </span>
              </div>
            </div>
            <div className="bg-[var(--accent)] p-4 md:p-5" data-id="element-106">
              <div className="flex items-center justify-between" data-id="element-107">
                <span className="font-bold text-base md:text-lg text-[var(--bg-deep)]" data-id="element-108">
                  Precio de los bonos hoy
                </span>
                <span className="font-bold text-xl md:text-2xl text-[var(--bg-deep)]" data-id="element-109">
                  GRATIS
                </span>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Closing Copy */}
        <motion.div initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} className="text-center mb-8" data-id="element-110">
          <h3 className="text-xl md:text-2xl font-serif text-[var(--text-heading)] mb-6" data-id="element-111">
            No te estoy regalando relleno.
          </h3>
          <div className="max-w-2xl mx-auto space-y-3 text-sm md:text-base text-[var(--text-primary)] leading-relaxed" data-id="element-112">
            <p data-id="element-113">
              Te estoy dando{' '}
              <strong className="text-[var(--text-heading)]" data-id="element-114">
                las 4 herramientas que hacen que el método funcione
              </strong>
              .
            </p>
            <p data-id="element-115">
              Sin el <strong className="text-[var(--accent)]" data-id="element-116">SOS Kit</strong>,
              te quedarás paralizado cuando llegue la crisis.
            </p>
            <p data-id="element-117">
              Sin el{' '}
              <strong className="text-[var(--accent)]" data-id="element-118">
                Mapa del Desierto
              </strong>
              , no sabrás por dónde empezar.
            </p>
            <p data-id="element-119">
              Sin el{' '}
              <strong className="text-[var(--accent)]" data-id="element-120">Journaling</strong>, el
              proceso se quedará en tu cabeza sin bajar al corazón.
            </p>
            <p data-id="element-121">
              Sin la{' '}
              <strong className="text-[var(--accent)]" data-id="element-122">
                Guía Anti-Retroceso
              </strong>
              , una caída te sacará del camino.
            </p>
            <p className="text-lg md:text-xl font-serif italic text-[var(--accent)] pt-4" data-id="element-123">
              Con los 4 bonos... tienes todo lo que necesitas para empezar hoy y
              llegar al final.
            </p>
          </div>
        </motion.div>

        {/* P.S. Section */}
        <div className="border-t border-[var(--border-subtle)] pt-8 space-y-4 text-sm md:text-base" data-id="element-124">
          <p className="text-[var(--text-primary)] leading-relaxed" data-id="element-125">
            <strong className="text-[var(--text-heading)]" data-id="element-126">P.D.:</strong> Si
            estás leyendo esto hasta aquí, es porque algo en ti sabe que lo
            necesita. No lo pienses más. A veces el primer paso de salir del
            fondo es simplemente tomar la decisión de empezar.{' '}
            <strong className="text-[var(--accent)]" data-id="element-127">
              Hoy puedes hacerlo.
            </strong>
          </p>
          <p className="text-[var(--text-primary)] leading-relaxed" data-id="element-128">
            <strong className="text-[var(--text-heading)]" data-id="element-129">P.D.D.:</strong> Los
            4 bonos son{' '}
            <strong className="text-[var(--accent)]" data-id="element-130">
              gratis solo porque los incluí para ti
            </strong>
            . No tienen precio separado porque no los vendo por separado. Son
            tuyos hoy con tu método.
          </p>
        </div>
      </div>
    </section>;
}